import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { client } from '../api/Api';
import { useNavigate } from 'react-router-dom';
import { getMe } from '../api/Api';

// Silhouette components using SVG files
const ManSilhouette = ({ size = 'normal' }) => {
  const height = size === 'large' ? '250px' : size === 'medium' ? '200px' : '150px';
  return (
    <img
      src="/img/man.svg"
      alt="Vyras"
      style={{ width: '100%', height, objectFit: 'contain', display: 'block' }}
    />
  );
};

const WomanSilhouette = ({ size = 'normal' }) => {
  const height = size === 'large' ? '250px' : size === 'medium' ? '200px' : '150px';
  return (
    <img
      src="/img/woman.svg"
      alt="Moteris"
      style={{ width: '100%', height, objectFit: 'contain', display: 'block' }}
    />
  );
};

const GirlSilhouette = ({ size = 'normal' }) => {
  const height = size === 'large' ? '200px' : size === 'medium' ? '170px' : '130px';
  return (
    <img
      src="/img/girl.svg"
      alt="Mergaitė"
      style={{ width: '100%', height, objectFit: 'contain', display: 'block' }}
    />
  );
};

const BoySilhouette = ({ size = 'normal' }) => {
  const height = size === 'large' ? '200px' : size === 'medium' ? '170px' : '130px';
  return (
    <img
      src="/img/boy.svg"
      alt="Berniukas"
      style={{ width: '100%', height, objectFit: 'contain', display: 'block' }}
    />
  );
};

// Helper function to render silhouette based on member type and gender
const renderMemberSilhouette = (member, size = 'normal') => {
  if (member.type === 'MAMA') {
    return <WomanSilhouette size={size} />;
  } else if (member.type === 'TETIS') {
    return <ManSilhouette size={size} />;
  } else if (member.type === 'VAIKAS') {
    return member.gender === 'FEMALE' ? <GirlSilhouette size={size} /> : <BoySilhouette size={size} />;
  } else if (member.type === 'SENELIS') {
    return <ManSilhouette size={size} />;
  } else if (member.type === 'SENELE') {
    return <WomanSilhouette size={size} />;
  }
  return null;
};

export default function FamilyContent() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [savingGender, setSavingGender] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [membersRes, userData] = await Promise.all([
        client.get('/family/members'),
        getMe(),
      ]);
      setMembers(membersRes.data.members || []);
      setUser(userData);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Klaida užkraunant duomenis.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGender = async (gender) => {
    try {
      setSavingGender(true);
      setError('');
      await client.post('/update-gender', { gender });
      const userData = await getMe();
      setUser(userData);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError(e.response?.data?.error || 'Nepavyko išsaugoti pasirinkimo');
      }
    } finally {
      setSavingGender(false);
    }
  };

  const handleAddMember = async (type, gender = null) => {
    try {
      setAdding(true);
      setError('');
      const data = {
        type: type,
        name: null,
        gender: gender || null,
        age: null,
        height: null,
        weight: null,
        activityLevel: null,
      };
      await client.post('/family/members', data);
      await loadData();
      setOpenAddDialog(false);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError(e.response?.data?.error || 'Nepavyko pridėti šeimos nario');
      }
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await client.delete(`/family/members/${id}`);
      await loadData();
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Nepavyko pašalinti šeimos nario');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If user hasn't selected gender yet, show selection screen
  if (!user || !user.gender) {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
          Šeimos nariai
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 4 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: savingGender ? 'wait' : 'pointer',
              opacity: savingGender ? 0.6 : 1,
              '&:hover': { opacity: savingGender ? 0.6 : 0.8 },
            }}
            onClick={() => !savingGender && handleSelectGender('MALE')}
          >
            <Box sx={{ width: '200px', mb: 2 }}>
              <ManSilhouette size="large" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Vyras
            </Typography>
            {savingGender && <CircularProgress size={24} sx={{ mt: 1 }} />}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: savingGender ? 'wait' : 'pointer',
              opacity: savingGender ? 0.6 : 1,
              '&:hover': { opacity: savingGender ? 0.6 : 0.8 },
            }}
            onClick={() => !savingGender && handleSelectGender('FEMALE')}
          >
            <Box sx={{ width: '200px', mb: 2 }}>
              <WomanSilhouette size="large" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Moteris
            </Typography>
            {savingGender && <CircularProgress size={24} sx={{ mt: 1 }} />}
          </Box>
        </Box>
      </Box>
    );
  }

  // User has selected gender, show family members in a row
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
        Šeimos nariai
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-end' }}>
        {/* Current user */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '150px' }}>
          <Box sx={{ width: '150px', mb: 1 }}>
            {user.gender === 'MALE' ? (
              <ManSilhouette size="medium" />
            ) : (
              <WomanSilhouette size="medium" />
            )}
          </Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Jūs
          </Typography>
          <IconButton
            color="primary"
            onClick={() => setOpenAddDialog(true)}
            disabled={adding}
            sx={{ mb: 1 }}
          >
            {adding ? <CircularProgress size={20} /> : <AddIcon />}
          </IconButton>
        </Box>

        {/* Family members */}
        {members.map((member) => (
          <Box
            key={member.id}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '150px' }}
          >
            <Box sx={{ width: '150px', mb: 1 }}>
              {renderMemberSilhouette(member, 'medium')}
            </Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {member.typeLabel || member.type}
            </Typography>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(member.id)}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      {/* Add member dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Pridėti šeimos narį</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              onClick={() => handleAddMember('MAMA')}
              disabled={adding}
              sx={{ py: 2 }}
            >
              Mama
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleAddMember('TETIS')}
              disabled={adding}
              sx={{ py: 2 }}
            >
              Tėtis
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleAddMember('VAIKAS', 'MALE')}
              disabled={adding}
              sx={{ py: 2 }}
            >
              Berniukas
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleAddMember('VAIKAS', 'FEMALE')}
              disabled={adding}
              sx={{ py: 2 }}
            >
              Mergaitė
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleAddMember('SENELIS')}
              disabled={adding}
              sx={{ py: 2 }}
            >
              Senelis
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleAddMember('SENELE')}
              disabled={adding}
              sx={{ py: 2 }}
            >
              Senelė
            </Button>
            <Button
              variant="text"
              onClick={() => setOpenAddDialog(false)}
              sx={{ mt: 2 }}
            >
              Atšaukti
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
